import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { IconButton } from "./icon-button";
import ConfirmDialog from "../ConfirmDialog";
import { cn } from "@/lib/utils";
import { useI18n } from "../../i18n";

/** What a dangerous item asks before it acts (П5): «Удалить «Х»?» + what goes with it + [Удалить] [Отмена]. */
export interface MenuConfirm {
  readonly title: string;
  readonly body?: string;
  /** The red button's words (default: the item's label without «…»). */
  readonly yes?: string;
  readonly no?: string;
}

export type MenuEntry =
  | {
      readonly label: string;
      readonly icon?: LucideIcon;
      readonly onSelect: () => void;
      /** Red text (delete). With `confirm` it asks first (ConfirmDialog, tone danger). */
      readonly danger?: boolean;
      readonly confirm?: MenuConfirm;
      readonly disabled?: boolean;
      readonly testid?: string;
      readonly hidden?: boolean;
    }
  | { readonly separator: true; readonly hidden?: boolean }
  /** A grey line of words: «Переименовать, порядок, вложение, удалить — в Подготовке». */
  | { readonly note: string; readonly hidden?: boolean; readonly testid?: string };

export const MENU_CLASS =
  "z-[var(--z-dropdown)] flex min-w-[13.5rem] max-w-80 flex-col rounded-md border border-line bg-panel-2 p-1 text-fg shadow-xl data-[state=open]:animate-in data-[state=open]:fade-in-0";
const ITEM_CLASS =
  "flex w-full cursor-default select-none items-center gap-2 rounded px-2 py-[5px] text-left text-body-s outline-none data-[highlighted]:bg-panel data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-[13px] [&_svg]:flex-none [&_svg]:text-dim";
/**
 * The confirmation of a dangerous item (П5 + windows step 1, 28.10): the one confirm window of the app,
 * `ConfirmDialog tone="danger"` — «Удалить «Х»?» + what goes with it + [Удалить] (solid red) [Отмена].
 */
export function MenuConfirmDialog({
  confirm,
  fallbackYes,
  onConfirm,
  onClose,
}: {
  readonly confirm: MenuConfirm | null;
  readonly fallbackYes: string;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <ConfirmDialog
      open={confirm !== null}
      onOpenChange={(open) => { if (!open) onClose(); }}
      tone="danger"
      title={confirm?.title ?? ""}
      description={confirm?.body ?? ""}
      confirmLabel={confirm?.yes ?? fallbackYes}
      cancelLabel={confirm?.no ?? t("sb.cancel")}
      onConfirm={onConfirm}
      onCancel={onClose}
    />
  );
}

const visible = (entries: readonly MenuEntry[]): MenuEntry[] => {
  const shown = entries.filter((e) => !e.hidden);
  // no separator first, last or doubled
  return shown.filter((e, i) => !("separator" in e) || (i > 0 && i < shown.length - 1 && !("separator" in (shown[i - 1] as MenuEntry))));
};

/**
 * «⋯» of a row or a header (П3/П5): every action written in words, a dangerous one in red; an item with `confirm`
 * asks in the confirm window before it acts. Not by itself an icon of its own colour — it is the second of the ≤2 icons.
 */
export function RowMenu({
  items,
  label,
  size = "sm",
  testid,
  contentTestid,
  align = "end",
  className,
  onOpenChange,
  icon,
}: {
  readonly items: readonly MenuEntry[];
  readonly label?: string;
  readonly size?: "md" | "sm";
  readonly testid?: string;
  readonly contentTestid?: string;
  readonly align?: "start" | "center" | "end";
  readonly className?: string;
  readonly onOpenChange?: (open: boolean) => void;
  /** The trigger's icon (default «⋯»): «＋» of a header that opens «Создать…». */
  readonly icon?: React.ReactNode;
}): React.JSX.Element | null {
  const { t } = useI18n();
  const words = label ?? t("sb.more");
  const [asking, setAsking] = React.useState<Extract<MenuEntry, { onSelect: () => void }> | null>(null);
  const keepFocus = React.useRef(false);
  const shown = visible(items);
  if (shown.length === 0) return null;
  return (
    <>
      <DropdownMenuPrimitive.Root modal={false} onOpenChange={onOpenChange}>
          <DropdownMenuPrimitive.Trigger asChild>
            <IconButton
              label={words}
              size={size}
              icon={icon ?? <MoreHorizontal size={14} aria-hidden="true" />}
              className={cn("row-menu-trigger", className)}
              data-testid={testid}
              noHint
              title={words}
            />
          </DropdownMenuPrimitive.Trigger>
        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            align={align}
            sideOffset={4}
            collisionPadding={8}
            className={MENU_CLASS}
            data-testid={contentTestid}
            onCloseAutoFocus={(e) => {
              if (keepFocus.current) {
                e.preventDefault();
                keepFocus.current = false;
              }
            }}
          >
            {shown.map((entry, i) => {
              if ("separator" in entry) return <DropdownMenuPrimitive.Separator key={`sep${i}`} className="my-[3px] h-px bg-line" />;
              if ("note" in entry)
                return (
                  <span key={`note${i}`} className="px-2 py-[5px] text-meta text-dim" data-testid={entry.testid}>
                    {entry.note}
                  </span>
                );
              const Icon = entry.icon;
              return (
                <DropdownMenuPrimitive.Item
                  key={`${entry.label}${i}`}
                  disabled={entry.disabled}
                  data-testid={entry.testid}
                  className={cn(ITEM_CLASS, entry.danger && "text-danger [&_svg]:text-danger")}
                  onSelect={() => {
                    if (entry.confirm) {
                      keepFocus.current = true;
                      setAsking(entry);
                    } else entry.onSelect();
                  }}
                >
                  {Icon ? <Icon aria-hidden="true" /> : <span className="w-[13px] flex-none" aria-hidden="true" />}
                  <span className="min-w-0 flex-1 break-words">{entry.label}</span>
                </DropdownMenuPrimitive.Item>
              );
            })}
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
      {/* Mounted only while asking: a long list does not carry a closed dialog per row. */}
      {asking !== null ? (
      <MenuConfirmDialog
        confirm={asking?.confirm ?? null}
        fallbackYes={asking?.label.replace(/…$/, "") ?? ""}
        onConfirm={() => {
          const act = asking?.onSelect;
          setAsking(null);
          act?.();
        }}
        onClose={() => setAsking(null)}
      />
      ) : null}
    </>
  );
}
